import type { AppSettings } from '@/core/types/settings'

/**
 * What a filled block says.
 *
 * Three pieces of information compete for one small rectangle: the event type,
 * the object, and whether there is a comment. The settings decide which wins,
 * because different people track differently -- someone logging projects wants
 * the project name; someone logging activities wants the activity.
 *
 * Pure, so the precedence rules are testable without rendering anything.
 */

export interface LabelInput {
  typeLabel: string
  objectName: string | null
  comment: string | null
}

export type LabelSettings = Pick<
  AppSettings,
  'hideTypeWhenObjectSet' | 'hideTypeAndObjectWhenComment'
>

export function blockLabel(input: LabelInput, settings: LabelSettings): string {
  const { typeLabel, objectName, comment } = input
  const hasComment = comment !== null && comment.trim().length > 0

  // A comment is the most specific thing a block can carry, so when the user
  // has asked for it, it replaces everything else.
  if (hasComment && settings.hideTypeAndObjectWhenComment) {
    return comment.trim()
  }

  if (objectName) {
    if (settings.hideTypeWhenObjectSet) return objectName
    if (!typeLabel) return objectName
    return `${typeLabel} · ${objectName}`
  }

  return typeLabel
}
